import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";

import React, { useState } from "react";

import styles from "./styles.module.css";

export interface CardItem {
    icon: string;
    label: string;
    value: string | Date | React.ReactNode;
    isDropdown?: boolean;
    onDropdownClick?: () => void;
    dropdownType?: "calendar" | "menu";
    dropdownOptions?: string[];
    rowStyle?: React.CSSProperties;
}

interface CustomCardProps {
    items: CardItem[];
    onSaveChanges?: () => void;
    onDropdownChange?: (index: number, value: string | Date) => void;
    editIcon?: string;
    saveIcon?: string;
    dropdownIcon?: string;
    height?: string;
    customStyle?: React.CSSProperties;
    gridTemplateColumns?: string;
}

const CustomCard: React.FC<CustomCardProps> = ({ items, onSaveChanges, onDropdownChange, editIcon, saveIcon, dropdownIcon, height, customStyle, gridTemplateColumns }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [activeDropdownIndex, setActiveDropdownIndex] = useState<number | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [activeDropdownType, setActiveDropdownType] = useState<"calendar" | "menu" | null>(null);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

    const handleButtonClick = () => {
        if (isEditing) {
            onSaveChanges?.();
        }
        setIsEditing(!isEditing);
    };

    const handleDropdownClick = (index: number, event: React.MouseEvent<HTMLElement>, dropdownType: "calendar" | "menu") => {
        setMenuAnchor(event.currentTarget);
        setActiveDropdownIndex(index);
        setActiveDropdownType(dropdownType);
        const currentValue = items[index]?.value;

        if (dropdownType === "calendar") {
            if (currentValue instanceof Date || typeof currentValue === "string" || typeof currentValue === "number") {
                const parsedDate = dayjs(currentValue);
                setSelectedDate(parsedDate.isValid() ? parsedDate : null);
            } else {
                setSelectedDate(null);
            }
        }
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setActiveDropdownIndex(null);
        setActiveDropdownType(null);
    };

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            setSelectedDate(date);
        }
    };
    const handleDateConfirm = () => {
        if (selectedDate && activeDropdownIndex !== null) {
            onDropdownChange?.(activeDropdownIndex, selectedDate.format("DD-MMM-YYYY"));
            closeMenu();
        }
    };

    const handleMenuSelect = (option: string) => {
        if (activeDropdownIndex !== null) {
            onDropdownChange?.(activeDropdownIndex, option);
            closeMenu();
        }
    };

    return (
        <div className={styles.customCard} style={{ height: height || "auto" }}>
            <div className={styles.container} style={{ gridTemplateColumns: gridTemplateColumns ?? "1fr 1fr minmax(12.5rem, auto)" }}>
                {items.map((item, index) => (
                    <div key={index} style={customStyle}>
                        <div className={styles.header}>
                            <img src={item.icon} alt={item.label} />
                            <span className={styles.text}>{item.label}</span>
                        </div>
                        <div className={styles.rowText} style={{ ...item.rowStyle, gap: "8px" }}>
                            {item.value instanceof Date ? item.value.toLocaleDateString() : item.value}
                            {isEditing && item.isDropdown && (
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    <img src={dropdownIcon} alt="dropdown" onClick={(event) => handleDropdownClick(index, event, item.dropdownType || "menu")} height={16} width={16} />
                                </span>
                            )}
                            {/* Render Dropdown (Calendar or Menu) */}
                            {activeDropdownIndex === index && activeDropdownType === "calendar" && (
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={handleDateConfirm}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateCalendar disablePast value={selectedDate?.isValid() ? selectedDate : null} onChange={(date) => handleDateChange(date)} />
                                        <div className={styles.dateConfirmContainer}>
                                            <button onClick={closeMenu} className={styles.dateActionButton}>
                                                CANCEL
                                            </button>
                                            <button onClick={handleDateConfirm} className={styles.dateActionButton}>
                                                OK
                                            </button>
                                        </div>
                                    </LocalizationProvider>
                                </Menu>
                            )}

                            {activeDropdownIndex === index && activeDropdownType === "menu" && (
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={closeMenu}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                >
                                    {item.dropdownOptions?.map((option, i) => (
                                        <MenuItem key={i} onClick={() => handleMenuSelect(option)}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {(editIcon || saveIcon) && (
                <div className={styles.editContainer}>
                    <button className={styles.editButton} onClick={handleButtonClick}>
                        <img src={isEditing ? saveIcon : editIcon} alt={isEditing ? "save" : "edit"} />
                        <span className={styles.editText}>{isEditing ? "Save Changes" : "EDIT"}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomCard;
